import React, { useEffect, useState } from "react";
import "../../styles/ProfileModal.css";
import {
  changeUserPassword,
  getUserProfile,
  updateUserProfile,
} from "../../api/userAuthApi";
import { getMyOrders } from "../../api/ordersApi";
import { getLearningDashboard } from "../../api/learningApi";

function normalizeUser(user) {
  return {
    ...user,
    phone: user?.phone || user?.mobile || "",
    avatar: user?.avatar || user?.image || "",
    socialLinks: {
      youtube: user?.socialLinks?.youtube || user?.youtube || "",
      instagram: user?.socialLinks?.instagram || user?.instagram || "",
      portfolio: user?.socialLinks?.portfolio || user?.portfolio || "",
    },
  };
}

function optimizeImage(value, maxDimension) {
  if (!value || !value.startsWith("data:image/") || value.length < 100000) {
    return Promise.resolve(value);
  }

  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    image.onerror = () => resolve(value);
    image.src = value;
  });
}

export default function ProfileModal({ currentUser, onClose, onUpdateUser }) {
  const [activeTab, setActiveTab] = useState("profile"); // "profile" | "orders" | "security"
  const [isEditing, setIsEditing] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [passwords, setPasswords] = useState({ current: "", next: "", confirm: "" });
  const [passwordMessage, setPasswordMessage] = useState("");
  const [profileMessage, setProfileMessage] = useState("");

  
  const initialUser = normalizeUser(currentUser || {});
  const [name, setName] = useState(initialUser.name || "");
  const [userId, setUserId] = useState(initialUser.id || null);
  const [phone, setPhone] = useState(initialUser.phone || "");
  const [address, setAddress] = useState(initialUser.address || "");
  const [avatar, setAvatar] = useState(initialUser.avatar || "");
  const [coverPhoto, setCoverPhoto] = useState(initialUser.coverPhoto || "");

  
  const [socialLinks, setSocialLinks] = useState({
    youtube: initialUser.socialLinks.youtube,
    instagram: initialUser.socialLinks.instagram,
    portfolio: initialUser.socialLinks.portfolio,
  });

  
  const [orders, setOrders] = useState([]);
  const [learningSummary, setLearningSummary] = useState({ enrolled: 0, watchTime: 0, completed: 0 });

  useEffect(() => {
    let active = true;
    Promise.allSettled([getUserProfile(), getMyOrders(), getLearningDashboard()])
      .then(([profileResult, ordersResult, learningResult]) => {
        if (!active) return;
        if (profileResult.status === "fulfilled") {
          const user = normalizeUser(profileResult.value.user);
          setUserId(user.id || null);
          setName(user.name || "");
          setPhone(user.phone || "");
          setAddress(user.address || "");
          setAvatar(user.avatar || "");
          setCoverPhoto(user.coverPhoto || "");
          setSocialLinks(user.socialLinks);
        }
        if (ordersResult.status === "fulfilled") setOrders(ordersResult.value);
        if (learningResult.status === "fulfilled") {
          const learning = learningResult.value;
          const courses = learning.courses || [];
          const watchTime = courses.reduce((total, course) => total + (course.lectures || []).filter((lecture) => lecture.isCompleted).reduce((minutes, lecture) => minutes + Number(lecture.duration || 0), 0), 0);
          setLearningSummary({ enrolled: courses.length, watchTime: (watchTime / 60).toFixed(1), completed: learning.completedCourses || 0 });
        }
      })
      .finally(() => active && setIsLoading(false));
    return () => { active = false; };
  }, []);

  const handleAvatarFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setAvatar(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCoverFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setCoverPhoto(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileMessage("");
    if (!userId) {
      setProfileMessage("User ID is missing. Please log in again.");
      return;
    }
    if (!name.trim()) {
      setProfileMessage("Name is required.");
      return;
    }
    try {
      const [optimizedAvatar, optimizedCover] = await Promise.all([
        optimizeImage(avatar, 600),
        optimizeImage(coverPhoto, 1400),
      ]);
      const response = await updateUserProfile({
        userId,
        name: name.trim(),
        address: address.trim(),
        avatar: optimizedAvatar,
        coverPhoto: optimizedCover,
        socialLinks,
      });
      if (!response.user) {
        throw new Error("Profile API returned no user data.");
      }
      const updated = normalizeUser(response.user);

      if (onUpdateUser) onUpdateUser(updated);
      setIsEditing(false);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3500);
    } catch (error) {
      setProfileMessage(error.message || "Unable to update profile.");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage("");
    if (passwords.next.length < 8 || passwords.next !== passwords.confirm) {
      setPasswordMessage("Use at least 8 characters and make both new passwords match.");
      return;
    }
    try {
      if (!userId) {
        setPasswordMessage("User ID is missing. Please log in again.");
        return;
      }
      const response = await changeUserPassword({
        userId,
        currentPassword: passwords.current,
        newPassword: passwords.next,
      });
      setPasswordMessage(response.success || "Password updated successfully.");
      setPasswords({ current: "", next: "", confirm: "" });
    } catch (error) {
      setPasswordMessage(error.message);
    }
  };

  const handleDiscard = () => {
    const user = normalizeUser(currentUser || {});
    setName(user.name || "");
    setPhone(user.phone || "");
    setAddress(user.address || "");
    setAvatar(user.avatar || "");
    setCoverPhoto(user.coverPhoto || "");
    setSocialLinks(user.socialLinks);
    setIsEditing(false);
  };

  return (
    <div className="cd-profile-modal-curtain">
      <div className="cd-profile-modal-backdrop" onClick={onClose} />

      
      {showToast && (
        <div className="cd-toast-notification">
          <span className="cd-toast-check">✓</span>
          <div>
            <h5>Profile Updated!</h5>
            <p>Your details have been saved successfully.</p>
          </div>
        </div>
      )}

      <div className="cd-profile-modal-card">
        
        <div className="cd-profile-modal-header">
          <div className="cd-profile-segmented-nav">
            <button
              type="button"
              className={`cd-seg-btn ${activeTab === "profile" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("profile");
                setIsEditing(false);
              }}
            >
              <span>👤</span> My Profile
            </button>
            <button
              type="button"
              className={`cd-seg-btn ${activeTab === "orders" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("orders");
                setIsEditing(false);
              }}
            >
              <span>🛍️</span> My Orders ({orders.length})
            </button>
            <button
              type="button"
              className={`cd-seg-btn ${activeTab === "security" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("security");
                setIsEditing(false);
              }}
            >
              <span>🔐</span> Security
            </button>
          </div>

          <button type="button" className="cd-profile-close-icon" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        
        {activeTab === "profile" && (
          <form onSubmit={handleSaveProfile} className="cd-profile-form-container">
            {/* HERO COVER PHOTO WITH CONDITIONAL UPLOAD BADGE */}
            <div className="cd-cover-hero-wrap">
              <img src={coverPhoto} alt="Cover Banner" className="cd-cover-img" />
              <div className="cd-cover-gradient-overlay" />

              {isEditing && (
                <label className="cd-btn-change-cover" title="Upload new cover image">
                  <span>📷</span> Change Cover
                  <input type="file" accept="image/*" onChange={handleCoverFileChange} style={{ display: "none" }} />
                </label>
              )}

              
              <div className="cd-identity-strip">
                <div className="cd-avatar-wrapper">
                  <img src={avatar} alt="Profile" className="cd-main-avatar-img" />
                  {isEditing && (
                    <label className="cd-avatar-camera-pill" title="Upload new avatar">
                      📷
                      <input type="file" accept="image/*" onChange={handleAvatarFileChange} style={{ display: "none" }} />
                    </label>
                  )}
                </div>

                <div className="cd-identity-text">
                  <div className="cd-identity-name-row">
                    <h3>{name || "Student Creator"}</h3>
                    <span className="cd-badge-verified">✓ Verified Creator</span>
                  </div>
                  <p>{currentUser?.email || "aman.creator@gmail.com"}</p>
                </div>
              </div>
            </div>

            
            <div className="cd-form-scrollable-body">
              
              <div className="cd-profile-stats-ribbon">
                <div className="cd-stat-mini-pill">
                  <strong>{learningSummary.enrolled}</strong>
                  <span>Courses Enrolled</span>
                </div>
                <div className="cd-stat-mini-pill">
                  <strong>{learningSummary.watchTime} hrs</strong>
                  <span>Watch Time</span>
                </div>
                <div className="cd-stat-mini-pill">
                  <strong>{learningSummary.completed}</strong>
                  <span>Certificates Earned</span>
                </div>
              </div>

              
              <div className="cd-section-group-card">
                <div className="cd-section-group-head">
                  <h4>Personal & Account Information</h4>
                  {!isEditing ? (
                    <span className="cd-mode-indicator view-mode">🔒 Viewing Mode</span>
                  ) : (
                    <span className="cd-mode-indicator edit-mode">✏️ Editing Mode</span>
                  )}
                </div>

                <div className="cd-input-box">
                  <label>Full Name</label>
                  <div className={`cd-input-wrapper ${!isEditing ? "cd-input-readonly" : ""}`}>
                    <span className="cd-input-icon">👤</span>
                    <input
                      type="text"
                      required
                      disabled={!isEditing}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter full name"
                    />
                  </div>
                </div>

                <div className="cd-dual-row">
                  <div className="cd-input-box">
                    <label>Registered Mobile</label>
                    <div className="cd-input-wrapper cd-input-locked">
                      <span className="cd-input-icon">📱</span>
                      <input type="text" disabled value={phone || "Not available"} />
                      <span className="cd-lock-tag" title="Phone is permanent">🔒</span>
                    </div>
                  </div>

                  <div className="cd-input-box">
                    <label>Registered Email</label>
                    <div className="cd-input-wrapper cd-input-locked">
                      <span className="cd-input-icon">✉️</span>
                      <input type="email" disabled value={currentUser?.email || "aman.creator@gmail.com"} />
                      <span className="cd-lock-tag" title="Email is permanent">🔒</span>
                    </div>
                  </div>
                </div>
              </div>

              
              <div className="cd-section-group-card">
                <div className="cd-section-group-head">
                  <h4>Creator Social Handles</h4>
                </div>

                <div className="cd-dual-row">
                  <div className="cd-input-box">
                    <label>YouTube Channel</label>
                    <div className={`cd-input-wrapper ${!isEditing ? "cd-input-readonly" : ""}`}>
                      <span className="cd-input-icon">▶️</span>
                      <input
                        type="text"
                        disabled={!isEditing}
                        value={socialLinks.youtube}
                        onChange={(e) => setSocialLinks({ ...socialLinks, youtube: e.target.value })}
                        placeholder="e.g. youtube.com/@channel"
                      />
                    </div>
                  </div>

                  <div className="cd-input-box">
                    <label>Instagram Handle</label>
                    <div className={`cd-input-wrapper ${!isEditing ? "cd-input-readonly" : ""}`}>
                      <span className="cd-input-icon">📸</span>
                      <input
                        type="text"
                        disabled={!isEditing}
                        value={socialLinks.instagram}
                        onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })}
                        placeholder="e.g. @username"
                      />
                    </div>
                  </div>
                </div>

                <div className="cd-input-box">
                  <label>Portfolio / Showreel URL</label>
                  <div className={`cd-input-wrapper ${!isEditing ? "cd-input-readonly" : ""}`}>
                    <span className="cd-input-icon">🌐</span>
                    <input
                      type="text"
                      disabled={!isEditing}
                      value={socialLinks.portfolio}
                      onChange={(e) => setSocialLinks({ ...socialLinks, portfolio: e.target.value })}
                      placeholder="e.g. https://myportfolio.com"
                    />
                  </div>
                </div>
              </div>

              
              <div className="cd-section-group-card">
                <div className="cd-section-group-head">
                  <h4>Billing & Shipping Address</h4>
                </div>

                <div className="cd-input-box">
                  <label>Complete Address</label>
                  <div className={`cd-input-wrapper textarea-wrap ${!isEditing ? "cd-input-readonly" : ""}`}>
                    <span className="cd-input-icon textarea-icon">📍</span>
                    <textarea
                      rows="3"
                      required
                      disabled={!isEditing}
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="House/Flat No, Area, City, State, PIN Code"
                    />
                  </div>
                </div>
              </div>
            </div>

            
            <div className="cd-profile-modal-footer">
              {profileMessage && <p className="cd-profile-error">{profileMessage}</p>}
              {!isEditing ? (
                <>
                  <button type="button" className="cd-btn-modal-cancel" onClick={onClose}>
                    Close
                  </button>
                  <button
                    type="button"
                    className="cd-btn-modal-save"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsEditing(true);
                    }}
                    disabled={isLoading}
                  >
                    ✏️ Edit Profile
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="cd-btn-modal-cancel"
                    onClick={handleDiscard}
                  >
                    Discard
                  </button>
                  <button type="submit" className="cd-btn-modal-save save-active">
                    💾 Save Changes
                  </button>
                </>
              )}
            </div>
          </form>
        )}

        
        {activeTab === "orders" && (
          <div className="cd-orders-modal-body">
            <div className="cd-orders-list-wrap">
              {orders.length === 0 && <div className="cd-empty-orders">No orders found for this account.</div>}
              {orders.map((order) => (
                <div key={order.id} className="cd-order-card">
                  <div className="cd-order-top-row">
                    <div>
                      <div className="cd-order-code-pill">{order.orderRef}</div>
                      <h4>{order.courseTitle || "Course purchase"}</h4>
                      <p className="cd-order-meta">
                        📅 {new Date(order.createdAt).toLocaleDateString("en-IN")} &nbsp;•&nbsp; 💳 {order.paymentMethod || "Online"}
                      </p>
                    </div>

                    <div className="cd-order-pricing-box">
                      <span className="cd-order-amt">₹{order.amount}</span>
                      <span className="cd-order-status-chip">✓ {order.orderStatus}</span>
                    </div>
                  </div>

                  <div className="cd-order-action-bar">
                    <button
                      type="button"
                      className="cd-btn-receipt-download"
                      onClick={() => alert(`📄 Invoice ${order.orderRef} is not available yet.`)}
                    >
                      📥 Download Tax Invoice (PDF)
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="cd-profile-modal-footer">
              <button type="button" className="cd-btn-modal-cancel" onClick={onClose} style={{ marginLeft: "auto" }}>
                Close Window
              </button>
            </div>
          </div>
        )}

        {activeTab === "security" && (
          <form onSubmit={handleChangePassword} className="cd-profile-form-container cd-security-form">
            <div className="cd-form-scrollable-body">
              <div className="cd-section-group-card">
                <div className="cd-section-group-head">
                  <h4>Change Password</h4>
                  <span className="cd-mode-indicator view-mode">🔒 Protected</span>
                </div>
                <p className="cd-security-help">Set a password for your account. OTP login remains available.</p>
                <div className="cd-input-box"><label>Current Password</label><input type="password" required value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} placeholder="Enter current password" /></div>
                <div className="cd-input-box"><label>New Password</label><input type="password" required value={passwords.next} onChange={(e) => setPasswords({ ...passwords, next: e.target.value })} placeholder="Minimum 8 characters" /></div>
                <div className="cd-input-box"><label>Confirm New Password</label><input type="password" required value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} placeholder="Repeat new password" /></div>
                {passwordMessage && <p className="cd-security-message">{passwordMessage}</p>}
              </div>
            </div>
            <div className="cd-profile-modal-footer"><button type="button" className="cd-btn-modal-cancel" onClick={onClose}>Close</button><button type="submit" className="cd-btn-modal-save save-active">Update Password</button></div>
          </form>
        )}
      </div>
    </div>
  );
}